import { useInfiniteQuery, useQuery, type QueryKey, type UseQueryOptions, type UseInfiniteQueryOptions, type QueryFunctionContext } from "@tanstack/react-query";

// Corrected: queryFn is now a required top-level prop for standard queries.
type UseDataQueryProps<TData> = {
  queryKey: QueryKey;
  queryFn: () => Promise<TData | null>;
  options?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>;
  pagination?: never;
};

type UseInfiniteDataQueryProps<TData> = {
  queryKey: QueryKey;
  queryFn: (context: QueryFunctionContext<QueryKey, number>) => Promise<TData[] | null>;
  options?: Omit<UseInfiniteQueryOptions<TData[] | null>, 'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'>;
  pagination: {
    pageSize: number;
  };
};

// This is a workaround to get the correct return type for the overloaded function.
// The implementation is correct, but TypeScript has trouble inferring the return type of overloaded functions.
// See: https://github.com/microsoft/TypeScript/issues/43846
type UseDataQueryResult<TData> = 
  | ReturnType<typeof useQuery<TData>>
  | (Omit<ReturnType<typeof useInfiniteQuery<TData[] | null>>, 'data'> & { data: TData[] });


export function useDataQuery<TData>(props: UseDataQueryProps<TData>): ReturnType<typeof useQuery<TData>>;
export function useDataQuery<TData>(props: UseInfiniteDataQueryProps<TData>): Omit<ReturnType<typeof useInfiniteQuery<TData[] | null>>, 'data'> & { data: TData[] };
export function useDataQuery<TData>(props: any): UseDataQueryResult<TData> {
  const { queryKey, queryFn, options, pagination } = props;

  if (pagination) {
    const { pageSize } = pagination;
    const infiniteQuery = useInfiniteQuery({
      queryKey,
      queryFn,
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        // Corrected: Safer check for lastPage
        return lastPage && Array.isArray(lastPage) && lastPage.length === pageSize ? allPages.length : undefined;
      },
      ...options,
    });

    return {
      ...infiniteQuery,
      data: infiniteQuery.data?.pages.flat().filter(Boolean) ?? [],
    } as Omit<ReturnType<typeof useInfiniteQuery<TData[] | null>>, 'data'> & { data: TData[] };
  }

  // This now works correctly as queryFn is a top-level prop.
  return useQuery({
    queryKey,
    queryFn,
    ...options,
  }) as ReturnType<typeof useQuery<TData>>;
}